import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { User } from '../../auth/types';

/**
 * Tabla de usuarios para SuperAdmin dashboard
 *
 * @remarks
 * Lista simple con bordes sutiles y role badges con colores.
 * Header sticky con glassmorphism.
 * Acciones inline (edit/delete).
 * Botón "Crear" navega a formulario fullscreen.
 *
 * @example
 * ```tsx
 * <UsersTable
 *   users={allUsers}
 *   loading={loading}
 *   onDelete={handleDelete}
 * />
 * ```
 */

interface UsersTableProps {
  users: User[];
  loading: boolean;
  onDelete: (userId: string) => void;
}

export function UsersTable({
  users,
  loading,
  onDelete,
}: UsersTableProps): JSX.Element {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = (userId: string) => {
    if (deletingId === userId) {
      // Confirmación - ejecutar delete
      onDelete(userId);
      setDeletingId(null);
    } else {
      // Primera vez - pedir confirmación
      setDeletingId(userId);
      setTimeout(() => setDeletingId(null), 3000); // Reset después de 3s
    }
  };

  const getRoleBadgeClass = (roleName: string): string => {
    if (roleName === 'admin') return 'role-admin';
    if (roleName === 'user_high') return 'role-high';
    if (roleName === 'user_medium') return 'role-medium';
    if (roleName === 'user_low') return 'role-low';
    return 'role-default';
  };

  const getRoleDisplayName = (roleName: string): string => {
    const roleKey = `users.roles.${roleName}`;
    return t(roleKey, { defaultValue: roleName });
  };

  if (loading) {
    return (
      <div className="table-loading">
        <div className="loading-skeleton" />
        <div className="loading-skeleton" />
        <div className="loading-skeleton" />
      </div>
    );
  }

  return (
    <div className="dashboard-table-container">
      <div className="dashboard-table-header">
        <h2 className="table-title">{t('users.title')}</h2>
        <button
          type="button"
          className="btn-create"
          onClick={() => navigate('/admin/users/new')}
        >
          {t('users.create')}
        </button>
      </div>

      {users.length === 0 ? (
        <div className="table-empty">
          <p>{t('users.empty')}</p>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/users/new')}>
            {t('users.createFirst')}
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dashboard-table">
            <thead className="table-head">
              <tr>
                <th>{t('users.table.email')}</th>
                <th>{t('users.table.name')}</th>
                <th>{t('users.table.tenant')}</th>
                <th>{t('users.table.role')}</th>
                <th>{t('users.table.lastLogin')}</th>
                <th className="table-actions-header">{t('users.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map((user) => {
                const isDeleting = deletingId === user.id;
                const isSuperAdmin = user.role.name === 'superadmin';
                const lastLogin = user.lastLoginAt
                  ? new Date(user.lastLoginAt).toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })
                  : '-';

                return (
                  <tr key={user.id} className="table-row">
                    <td className="table-cell-email">{user.email}</td>
                    <td className="table-cell-name">{user.fullName}</td>
                    <td className="table-cell-tenant">{user.tenant.name}</td>
                    <td className="table-cell-role">
                      <span className={`role-badge ${getRoleBadgeClass(user.role.name)}`}>
                        {getRoleDisplayName(user.role.name)}
                      </span>
                    </td>
                    <td className="table-cell-date">{lastLogin}</td>
                    <td className="table-cell-actions">
                      {!isSuperAdmin ? (
                        <>
                          <button
                            type="button"
                            className="btn-table-action btn-edit"
                            onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                            title={t('users.editTooltip')}
                          >
                            {t('users.edit')}
                          </button>
                          <button
                            type="button"
                            className={`btn-table-action btn-delete ${isDeleting ? 'confirming' : ''}`}
                            onClick={() => handleDelete(user.id)}
                            title={isDeleting ? t('users.confirmTooltip') : t('users.deleteTooltip')}
                          >
                            {isDeleting ? t('users.confirm') : t('users.delete')}
                          </button>
                        </>
                      ) : (
                        <span className="table-protected">{t('users.protected')}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
